"use client"

import * as React from "react"
import { ArrowDownIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface MessageScrollerContextValue {
  isAtBottom: boolean
  scrollToBottom: () => void
  viewportRef: React.RefObject<HTMLDivElement | null>
  setIsAtBottom: (value: boolean) => void
}

const MessageScrollerContext = React.createContext<MessageScrollerContextValue>({
  isAtBottom: true,
  scrollToBottom: () => {},
  viewportRef: { current: null },
  setIsAtBottom: () => {},
})

function useMessageScroller() {
  return React.useContext(MessageScrollerContext)
}

function MessageScrollerProvider({ children }: { children: React.ReactNode }) {
  const [isAtBottom, setIsAtBottom] = React.useState(true)
  const viewportRef = React.useRef<HTMLDivElement>(null)

  const scrollToBottom = React.useCallback(() => {
    const el = viewportRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  return (
    <MessageScrollerContext.Provider value={{ isAtBottom, setIsAtBottom, viewportRef, scrollToBottom }}>
      {children}
    </MessageScrollerContext.Provider>
  )
}

const MessageScroller = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("relative flex flex-1 flex-col overflow-hidden", className)} {...props} />
  ),
)
MessageScroller.displayName = "MessageScroller"

const MessageScrollerViewport = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, onScroll, children, ...props }, ref) => {
    const { viewportRef, setIsAtBottom, scrollToBottom, isAtBottom } = useMessageScroller()

    const handleScroll = React.useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
        setIsAtBottom(distFromBottom < 64)
        ;(onScroll as React.UIEventHandler<HTMLDivElement> | undefined)?.(e)
      },
      [onScroll, setIsAtBottom],
    )

    // Auto-scroll when content changes if already at bottom
    const lastScrollHeight = React.useRef(0)
    React.useEffect(() => {
      const el = viewportRef.current
      if (!el) return
      const observer = new MutationObserver(() => {
        if (isAtBottom && el.scrollHeight !== lastScrollHeight.current) {
          lastScrollHeight.current = el.scrollHeight
          scrollToBottom()
        }
      })
      observer.observe(el, { childList: true, subtree: true, characterData: true })
      return () => observer.disconnect()
    }, [isAtBottom, scrollToBottom, viewportRef])

    return (
      <div
        ref={(node) => {
          ;(viewportRef as React.MutableRefObject<HTMLDivElement | null>).current = node
          if (typeof ref === "function") ref(node)
          else if (ref) ref.current = node
        }}
        className={cn("flex-1 overflow-y-auto", className)}
        onScroll={handleScroll}
        {...props}
      >
        {children}
      </div>
    )
  },
)
MessageScrollerViewport.displayName = "MessageScrollerViewport"

const MessageScrollerContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="log"
      aria-relevant="additions"
      aria-live="polite"
      className={cn("flex flex-col", className)}
      {...props}
    />
  ),
)
MessageScrollerContent.displayName = "MessageScrollerContent"

function MessageScrollerButton({ className }: { className?: string }) {
  const { isAtBottom, scrollToBottom } = useMessageScroller()

  if (isAtBottom) return null

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-3">
      <button
        type="button"
        aria-label="Ir para o final"
        onClick={scrollToBottom}
        className={cn(
          "pointer-events-auto flex size-8 items-center justify-center rounded-full border bg-background shadow-md transition-opacity hover:opacity-90",
          className,
        )}
      >
        <ArrowDownIcon className="size-4 text-muted-foreground" />
        <span className="sr-only">Ir para o final</span>
      </button>
    </div>
  )
}

export {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerButton,
  useMessageScroller,
}
